import React, { Component, createRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Hls from "hls.js";

gsap.registerPlugin(ScrollTrigger);

const HLS_BASE = "/hls";
const TOTAL_CLIPS = 7;
const START_INDEX = 2; // Clip_3 = "Combes drone _Sub_02.mp4"

export default class Video extends Component {
  constructor(props) {
    super(props);

    this.state = {
      active: 1,           // which slot is currently on screen (1 or 2)
      slot1Ready: false,   // slot 1 video has canplay data
      slot2Ready: false,   // slot 2 video has canplay data
      firstvideoLoaded: false,
      // The poster to show behind the currently active slot
      activePoster: this.getPosterUrl(START_INDEX),
    };

    this.videoRef1 = createRef();
    this.videoRef2 = createRef();
    this.hls1 = null;
    this.hls2 = null;
    this.slotClipIndex = { 1: START_INDEX, 2: START_INDEX + 1 };
    this.isSwitching = false;

    this.onEnded1 = this.onEnded1.bind(this);
    this.onEnded2 = this.onEnded2.bind(this);
    this.initTriggers();
  }

  getPosterUrl(index) {
    const clipNum = (index % TOTAL_CLIPS) + 1;
    return `${HLS_BASE}/Clip_${clipNum}/poster.jpg`;
  }

  getHlsUrl(index) {
    const clipNum = (index % TOTAL_CLIPS) + 1;
    return `${HLS_BASE}/Clip_${clipNum}/master.m3u8`;
  }

  initTriggers() {
    ScrollTrigger.create({
      trigger: "#hero",
      start: "0 0",
      end: "100% 0%",
      onEnterBack: () => {
        try {
          const activeRef = this.state.active === 1 ? this.videoRef1 : this.videoRef2;
          activeRef.current?.play().catch(() => {});
        } catch (e) {}
      },
      onLeave: () => {
        try {
          this.videoRef1.current?.pause();
          this.videoRef2.current?.pause();
        } catch (e) {}
      },
    });
  }

  attachHls(videoEl, hlsSrc, onReady) {
    if (!videoEl) return null;
    let hls = null;

    const handleReady = () => {
      videoEl.removeEventListener("canplay", handleReady);
      if (onReady) onReady();
    };
    videoEl.addEventListener("canplay", handleReady);

    // Safari native HLS
    if (videoEl.canPlayType("application/vnd.apple.mpegurl") && !Hls.isSupported()) {
      videoEl.src = hlsSrc;
      videoEl.load();
    } else if (Hls.isSupported()) {
      hls = new Hls({
        startLevel: -1,
        capLevelToPlayerSize: true,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        abrEwmaDefaultEstimate: 1000000,
      });
      hls.loadSource(hlsSrc);
      hls.attachMedia(videoEl);
    }
    return hls;
  }

  componentDidMount() {
    // Initial mount: load first clip into Slot 1 and second clip into Slot 2
    this.hls1 = this.attachHls(
      this.videoRef1.current,
      this.getHlsUrl(this.slotClipIndex[1]),
      () => {
        this.setState({ slot1Ready: true, firstvideoLoaded: true });
        this.props.callbackVideoPlaying();
        // Background load slot 2
        this.hls2 = this.attachHls(
          this.videoRef2.current,
          this.getHlsUrl(this.slotClipIndex[2]),
          () => this.setState({ slot2Ready: true })
        );
      }
    );
  }

  componentWillUnmount() {
    if (this.hls1) this.hls1.destroy();
    if (this.hls2) this.hls2.destroy();
  }

  switchSlots() {
    if (this.isSwitching) return;
    this.isSwitching = true;

    const prevActive = this.state.active;
    const nextActive = prevActive === 1 ? 2 : 1;
    const nextInactive = prevActive; // The one we just finished playing

    // The clip index we want to preload NEXT into the inactive slot
    const nextClipToPreload = Math.max(this.slotClipIndex[1], this.slotClipIndex[2]) + 1;

    // 1. Swap active display
    this.setState({
      active: nextActive,
      // Update the poster for the NEXT transition
      activePoster: this.getPosterUrl(this.slotClipIndex[nextActive])
    });

    // 2. Play the already ready next video
    const nextVideoEl = nextActive === 1 ? this.videoRef1.current : this.videoRef2.current;
    if (nextVideoEl) {
      nextVideoEl.currentTime = 0;
      nextVideoEl.play().catch(() => {});
    }

    // 3. Mark the now-inactive slot as NOT ready while we swap its source
    if (nextInactive === 1) {
      this.setState({ slot1Ready: false });
      if (this.hls1) this.hls1.destroy();
      this.slotClipIndex[1] = nextClipToPreload;
      this.hls1 = this.attachHls(
        this.videoRef1.current,
        this.getHlsUrl(nextClipToPreload),
        () => this.setState({ slot1Ready: true })
      );
    } else {
      this.setState({ slot2Ready: false });
      if (this.hls2) this.hls2.destroy();
      this.slotClipIndex[2] = nextClipToPreload;
      this.hls2 = this.attachHls(
        this.videoRef2.current,
        this.getHlsUrl(nextClipToPreload),
        () => this.setState({ slot2Ready: true })
      );
    }

    this.isSwitching = false;
  }

  onEnded1() {
    if (this.state.active === 1) this.switchSlots();
  }
  onEnded2() {
    if (this.state.active === 2) this.switchSlots();
  }

  render() {
    const { active, slot1Ready, slot2Ready, firstvideoLoaded, activePoster } = this.state;

    // Show spinner when the active slot's video isn't ready yet
    const activeSlotReady = active === 1 ? slot1Ready : slot2Ready;
    const showSpinner = !activeSlotReady;

    return (
      <div
        className="backgroundVideo"
        style={{ width: "100vw", height: "100vh", position: "absolute", top: 0, left: 0, zIndex: -1, overflow: "hidden" }}
      >

        {/*
          ─── LAYER 1: Poster image ───────────────────────────────────────────
          Low-res blurred image to prevent white screen during load.
        */}
        <img
          src={activePoster}
          alt="Loading preview"
          style={{
            position: "absolute",
            top: 0, left: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            filter: "blur(20px)",
            zIndex: 1,
          }}
        />

        {/*
          ─── LAYER 2: Spinner ────────────────────────────────────────────────
          Shown on top of the blurred poster while the video loads.
        */}
        {showSpinner && (
          <div
            style={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 5,
            }}
          >
            <style>
              {`
                @keyframes spinLoader { 100% { transform: rotate(360deg); } }
              `}
            </style>
            <div style={{
              width: "80px",
              height: "80px",
              border: "6px solid rgba(255, 255, 255, 0.3)",
              borderTop: "6px solid #ffffff",
              borderRadius: "50%",
              animation: "spinLoader 1s linear infinite"
            }} />
          </div>
        )}

        {/*
          ─── LAYER 3: Active Video Elements ──────────────────────────────────
          Two dedicated elements to swap between seamlessly.
        */}
        <video
          ref={this.videoRef1}
          autoPlay
          muted
          playsInline
          onEnded={this.onEnded1}
          style={{
            position: "absolute",
            top: 0, left: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            zIndex: active === 1 && slot1Ready ? 10 : 2,
            opacity: active === 1 && slot1Ready ? 1 : 0,
            transition: "opacity 0.5s ease"
          }}
        />
        <video
          ref={this.videoRef2}
          autoPlay
          muted
          playsInline
          onEnded={this.onEnded2}
          style={{
            position: "absolute",
            top: 0, left: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            zIndex: active === 2 && slot2Ready ? 10 : 2,
            opacity: active === 2 && slot2Ready ? 1 : 0,
            transition: "opacity 0.5s ease"
          }}
        />
      </div>
    );
  }
}
